import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { customerPaymentService } from '../services/customerPaymentService';
import CurrencyTextField, { formatCurrency } from './CurrencyTextField';
import { formatDateOnly } from '../utils/dateUtils';

/**
 * Apply unallocated payment balance to one or more open invoices (any order for the customer).
 */
const ApplyPaymentAllocationsDialog = ({
  open,
  onClose,
  onSuccess,
  payment,
  maxAmount
}) => {
  const [openInvoices, setOpenInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const cap = Math.max(0, parseFloat(maxAmount) || 0);

  useEffect(() => {
    if (!open || !payment?.customer_id) return;
    setError('');
    setAllocations({});
    loadInvoices();
  }, [open, payment?.customer_id]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const res = await customerInvoiceService.getOpenByCustomerId(payment.customer_id);
      setOpenInvoices(res?.data ?? []);
    } catch (err) {
      setOpenInvoices([]);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const totalToApply = () =>
    Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const invoiceGroups = useMemo(() => {
    const byOrder = new Map();
    for (const inv of openInvoices) {
      const key = inv.work_order_id != null ? String(inv.work_order_id) : 'none';
      if (!byOrder.has(key)) {
        byOrder.set(key, {
          label:
            inv.work_order_identifier ||
            inv.work_order_number ||
            (inv.work_order_id != null ? `Order #${inv.work_order_id}` : 'No order'),
          invoices: []
        });
      }
      byOrder.get(key).invoices.push(inv);
    }
    return Array.from(byOrder.values());
  }, [openInvoices]);

  const handleApply = async () => {
    const list = Object.entries(allocations)
      .filter(([, v]) => v && parseFloat(v) > 0)
      .map(([invId, v]) => ({ invoice_id: parseInt(invId, 10), amount: parseFloat(v) }));
    const sum = list.reduce((s, a) => s + a.amount, 0);
    if (list.length === 0) {
      setError('Enter at least one apply amount.');
      return;
    }
    if (sum > cap + 0.01) {
      setError(`Cannot apply more than ${formatCurrency(cap)} available on this payment.`);
      return;
    }
    try {
      setSaving(true);
      setError('');
      for (const alloc of list) {
        await customerPaymentService.addAllocation(payment.id, alloc.invoice_id, alloc.amount);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply payment');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => formatDateOnly(d, 'MM/dd/yyyy') || '—';
  const invoiceLabel = (inv) =>
    inv?.identifier?.trim() || inv?.invoice_number?.trim() || (inv?.id != null ? `#${inv.id}` : '—');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Apply payment to invoices</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Available on this payment: {formatCurrency(cap)} — Entering: {formatCurrency(totalToApply())}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : invoiceGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No open invoices for this customer.
          </Typography>
        ) : (
          invoiceGroups.map((group) => (
            <Box key={group.label} sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {group.label}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'surfaceMuted.main' }}>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Apply</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{invoiceLabel(inv)}</TableCell>
                      <TableCell>{formatDate(inv.due_date)}</TableCell>
                      <TableCell align="right">{formatCurrency(inv.balance_due)}</TableCell>
                      <TableCell align="right">
                        <CurrencyTextField
                          size="small"
                          value={allocations[inv.id] ?? ''}
                          onChange={(e) => {
                            const num = parseFloat(e.target.value);
                            setAllocations((prev) => ({
                              ...prev,
                              [inv.id]: Number.isFinite(num) ? num : ''
                            }));
                          }}
                          minimumFractionDigits={2}
                          maximumFractionDigits={2}
                          dollarAdornment
                          inputProps={{
                            min: 0,
                            max: Math.min(parseFloat(inv.balance_due) || 0, cap),
                            step: '0.01'
                          }}
                          sx={{ width: 132 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={saving || loading}>
          {saving ? <CircularProgress size={20} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyPaymentAllocationsDialog;
